import * as Sentry from '@sentry/react'
import { isDev, logger } from '@tunasong/models'
import { useEffect, useState } from 'react'
import { z } from 'zod'

const env = z
  .object({
    VITE_SENTRY_DSN: z.string().optional(),
  })
  .parse(import.meta.env)

export const sentryStyles = {
  '#sentry-feedback': {
    '--page-margin': '40px',
  },
}

export const useSentry = ({ mode, email, name }: { mode: 'light' | 'dark'; email?: string; name?: string }) => {
  const [initialized, setInitialized] = useState(false)
  useEffect(() => {
    if (!env.VITE_SENTRY_DSN) {
      logger.debug('No Sentry DSN found in manifest  - skipping Sentry initialization')
      return
    }
    if (initialized) {
      return
    }

    /** @see https://docs.sentry.io/platforms/javascript/guides/react/user-feedback/ */

    // disable feedback for mobile devices since it obscures the UI
    // const feedbackIntegration = Sentry.feedbackIntegration({
    //   // Additional SDK configuration goes in here, for example:
    //   colorScheme: mode,

    //   useSentryUser: {
    //     email: 'email',
    //     name: 'name',
    //   },
    //   isNameRequired: false,
    // })

    Sentry.setUser({
      email,
      name,
    })
    Sentry.init({
      dsn: env.VITE_SENTRY_DSN,
      // @todo we get Error: Multiple Sentry Session Replay instances are not supported
      integrations: [
        Sentry.browserTracingIntegration() /*feedbackIntegration*/ /* Sentry.replayIntegration() */,
      ].filter(Boolean),

      ignoreErrors: [
        // Benign post-unmount race in slate-react: a queued throttle/debounce of
        // onDOMSelectionChange fires after EDITOR_TO_ELEMENT has been cleared, so
        // toDOMNode/toSlateNode throws. No user impact; same code in 0.117 and 0.124.
        /Cannot resolve a DOM node from Slate node/,
        /Cannot resolve a Slate node from DOM node/,
        // Browser/environment noise that doesn't represent a Tunasong bug.
        // AbortError fires whenever a fetch/AbortController is intentionally cancelled
        // (route change, cleanup). NotReadableError surfaces from OS-level I/O failures
        // on file/storage access. The IndexedDB transaction error happens when a request
        // outlives its transaction — typical during browser-tab unmount.
        /AbortError/,
        /NotReadableError/,
        /Attempt to get records from database without an in-progress transaction/,
        // Stale chunk after a deploy: the user's tab still references hashed bundle
        // names that have since been replaced. Resolves on next page load.
        /Importing a module script failed/,
        /Failed to fetch dynamically imported module/,
        // Unhandled RTK Query rejection — the auth flow can race a 401 against
        // our session refresh. Sentry surfaces these as "Object captured as
        // promise rejection with keys: error, meta" with no stack frame.
        // Tracked separately as an opportunity to add a global try/catch on
        // unwrap() callers, but the rejection itself is recoverable.
        /Object captured as promise rejection with keys: error, meta/,
      ],

      /** @todo adjust the percentage of errors to be posted */
      tracesSampleRate: 1.0,
      /** @todo reduce to e.g., 0.1 when stable */
      replaysSessionSampleRate: 1.0, // This sets the sample rate at 100%. You may want to change it to 100% while in development and then sample at a lower rate in production.
      replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
    })
    setInitialized(true)
  }, [email, initialized, name, mode])
}
